import {
  Badge,
  BodyText,
  Card,
  Heading,
  LinkText,
  TabPanel,
} from '@postscript/components';
import ColorWidget from '../../Layout/ColorWidget';
import {
  StyledTabPanelIntro,
  StyledTabPanelText,
  TwoColumnLayout,
} from '../../Layout/SimpleStyledComponents';
import { FoundationsProps } from '../Foundations';

const FoundationsColor = ({ selected }: FoundationsProps): JSX.Element => {
  return (
    <TabPanel id="foundations-color" selected={selected}>
      <StyledTabPanelIntro>
        <BodyText size="medium">
          Our brand purple joins a series of bright supporting hues over a
          tinted grayscale. A range of &quot;suggested colors&quot; are selected
          from each hue.
        </BodyText>
      </StyledTabPanelIntro>

      <TwoColumnLayout margin="0 0 var(--spacing-4)">
        <Card size="large">
          <ColorWidget />
        </Card>
        <div>
          <StyledTabPanelText>
            <Heading size="xx-small">Creating the palette</Heading>
            <BodyText size="small">
              The full palette was built by first choosing our brand color
              (purple), and creating a purple-tinted grayscale with 11 values,
              0-10. From there, we settled on our our other brand colors
              (purple, blue, mint, green, yellow, orange, red, and pink) and
              created a value scale that aligns each hue to its corresponding
              grayscale value.
            </BodyText>
            <BodyText size="small">
              Desaturated hue values align with a corresponding grayscale range.
              We extrapolate outward to add additional shades of each. This
              creates a full palette, usable in any promotional design or
              illustration.
            </BodyText>
            <BodyText size="small">
              To trim down the color values in-use and to create a more
              utilitarian palette, we suggest a limited range of corresponding
              color styles to work with (see the card to the left).
            </BodyText>
          </StyledTabPanelText>
          <StyledTabPanelText>
            <Heading size="xx-small">Accessibility</Heading>
            <BodyText size="small">
              Evaluate your designs for{' '}
              <LinkText
                href="https://www.getstark.co/blog/accessible-contrast-ratios-and-a-levels-explained"
                target="_blank"
                rel="noreferrer"
              >
                proper contrast
              </LinkText>
              ,{' '}
              <LinkText
                href="https://www.secretstache.com/blog/designing-for-color-blind-users"
                target="_blank"
                rel="noreferrer"
              >
                color blindness
              </LinkText>
              , and the use of{' '}
              <LinkText
                href="https://polaris.shopify.com/foundations/content/accessible-and-inclusive-language"
                target="_blank"
                rel="noreferrer"
              >
                exclusive language
              </LinkText>
              . Our team uses tools such as{' '}
              <LinkText
                href="https://www.getstark.co"
                target="_blank"
                rel="noreferrer"
              >
                Stark
              </LinkText>{' '}
              to make accessibility in Figma easier.
            </BodyText>
            <BodyText size="small">
              Each hue uses a range of 11 values, 0-10. 0 is white, 10 is black,
              and 5 is the accessible middleground between the two. The
              palette&apos;s 0-10 range ensures that WCAG contrast standards are
              easily maintained. Matching a color with another at least 7 steps
              away ensures a strong 7:1 AAA contrast rating (though 5-6 steps is
              typically enough to meet at least a 4.5:1 AA rating).
            </BodyText>
          </StyledTabPanelText>
          <StyledTabPanelText>
            <Heading size="xx-small">
              Dark mode{' '}
              <Badge size="small" variant="success">
                Beta
              </Badge>
            </Heading>
            <BodyText size="small">
              Providing an alternative dark UI is important for relieving eye
              strain, improving energy consumption, and respecting users&apos;
              system settings. Our system strives to make dark mode easy to
              attain, but test your designs on both light and dark backgrounds.
            </BodyText>
          </StyledTabPanelText>
        </div>
      </TwoColumnLayout>
    </TabPanel>
  );
};

export default FoundationsColor;
